import { Card, CardBody, RadioGroup, ScrollShadow, Spinner, Tab, Tabs } from "@nextui-org/react";
import { useCallback } from "react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import TableHistoryConsumption from "../../../../components/fragments/admin/report/consumption/TableHistoryConsumption";
import useTableConsumption from "../../../../hooks/report/consumption/useTableConsumption";
import CardRadioButton from "../../../../components/fragments/admin/report/CardRadioButton.js";
import { filteredMaterialData, hasValidRole } from "../../../../services/helpers";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import ModalAddMaterial from "../../../../components/fragments/admin/report/ModalAddMaterial";
import RolesAccess from "../../../../enums/roles";
import useAuth from "../../../../hooks/useAuth";
import TableReportPreview from "../../../../components/fragments/admin/report/preview/TableReportPreview";
import { deleteWithFormFetcherTest } from "../../../../services/api";

// =====================================================================
// ENDPOINT
//
// Hanya aksi hapus. Bekerja di level BAHAN, bukan per baris: satu bahan punya
// satu baris pelaporan per bulan, jadi menghapus lewat loop dari frontend
// berisiko gagal separuh jalan dan meninggalkan data setengah terhapus.
// Lihat backend-additions.ts untuk implementasinya.
// =====================================================================
const deleteMaterialApi = (payload) => deleteWithFormFetcherTest("/api/pelaporan-penggunaan-bahan-b3/material", payload);

export default function IndexPage() {
  const { roles } = useAuth();
  const {
    table: { columnsTableActivePeriod, selectedCompany, setSelectedCompany, selectedMaterial, setSelectedMaterial, selectedPeriod, setSelectedPeriod },
    dataFetchTable: { data, isLoading, mutate },
    dataFetch: { dataCompany, isLoadingCompany, dataPeriods, isLoadingPeriods },
  } = useTableConsumption();

  const isAdminOrPengelola = hasValidRole(roles, [RolesAccess.PENGELOLA, RolesAccess.SUPER_ADMIN]);

  const materialData = filteredMaterialData(data, isLoading);

  /**
   * Ambil baris pelaporan milik satu bahan.
   *
   * Berbeda dengan pelaporan pembelian, di sini tidak ada fallback registrasiId
   * karena PelaporanPenggunaanBahanB3 hanya punya dataBahanB3Id.
   */
  const getRowsForMaterial = useCallback(
    (materialId) => {
      const rows = data?.data ?? [];
      if (!materialId) return [];
      return rows.filter((item) => item.dataBahanB3Id === materialId);
    },
    [data?.data],
  );

  const filteredData = selectedMaterial && !isLoading && data ? getRowsForMaterial(selectedMaterial) : [];

  // Hapus seluruh baris pelaporan bahan tsb, lalu bersihkan pilihan aktif.
  const handleDeleteMaterial = useCallback(
    async (material) => {
      if (getRowsForMaterial(material?.id).length === 0) {
        throw new Error("Tidak ada baris pelaporan untuk bahan ini");
      }

      await deleteMaterialApi({
        companyId: selectedCompany?.value,
        periodId: selectedPeriod?.value,
        dataBahanB3Id: material?.id,
      });

      // Kosongkan pilihan bila bahan yang dihapus sedang dipilih, agar tabel di
      // sebelah kanan tidak menampilkan data yang sudah tidak ada.
      if (selectedMaterial === material?.id) setSelectedMaterial(null);

      await mutate();
    },
    [getRowsForMaterial, mutate, selectedCompany?.value, selectedPeriod?.value, selectedMaterial, setSelectedMaterial],
  );

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardBody>
          <Tabs
            color="primary"
            variant="underlined"
            aria-label="tabs"
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
              cursor: "w-full",
              tab: "max-w-fit px-1.5 h-12",
              tabContent: "group-data-[selected=true]:font-semibold",
            }}
          >
            {isAdminOrPengelola && (
              <Tab key="1" title="Periode Aktif">
                <div className="h-[550px] mt-3">
                  <div className="flex justify-between gap-2 mb-5">
                    <div className="flex gap-2">
                      <div className="flex flex-col justify-end">
                        <ModalAddMaterial
                          endpoint="/api/pelaporan-penggunaan-bahan-b3/generate"
                          mutate={mutate}
                          type="Penggunaan"
                          setSelectedCompany={setSelectedCompany}
                          setSelectedMaterial={setSelectedMaterial}
                          setSelectedPeriod={setSelectedPeriod}
                        />
                      </div>
                    </div>
                    <div className="w-full flex justify-end items-center gap-2">
                      <div className="w-1/4">
                        <FilterReactSelect
                          defaultValue={selectedPeriod}
                          placeholder="Cari Periode"
                          options={dataPeriods?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingPeriods}
                          setValue={setSelectedPeriod}
                        />
                      </div>
                      <div className="w-1/4">
                        <FilterReactSelect
                          defaultValue={selectedCompany}
                          placeholder="Cari Perusahaan"
                          options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingCompany}
                          setValue={setSelectedCompany}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-1 h-[420px]">
                      {!data ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <h3>Tidak ada data</h3>
                        </div>
                      ) : isLoading ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <Spinner className="mx-auto" />
                        </div>
                      ) : data?.data?.length === 0 ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <h3>Tidak ada data</h3>
                        </div>
                      ) : (
                        <ScrollShadow hideScrollBar className="h-full">
                          <RadioGroup value={selectedMaterial} onValueChange={setSelectedMaterial}>
                            {materialData?.map((item) => (
                              <CardRadioButton
                                key={item?.id}
                                value={item?.id}
                                description={item}
                                canManage={isAdminOrPengelola}
                                showEdit={false}
                                deleteWarning="beserta seluruh baris pelaporan penggunaannya pada periode aktif?"
                                onDelete={handleDeleteMaterial}
                              />
                            ))}
                          </RadioGroup>
                        </ScrollShadow>
                      )}
                    </div>
                    <div className="col-span-3 h-[420px]">
                      <ClientDataGrid
                        columns={columnsTableActivePeriod}
                        rows={selectedMaterial ? filteredData : []}
                        isLoading={isLoading}
                        initialState={{
                          sorting: {
                            sortModel: [{ field: "id", sort: "asc" }],
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Tab>
            )}
            {isAdminOrPengelola && (
              <Tab key="2" title="Preview">
                <TableReportPreview
                  isPreviewOnly
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={setSelectedPeriod}
                />
              </Tab>
            )}
            <Tab key="3" title="Riwayat">
              <TableHistoryConsumption
                dataCompany={dataCompany}
                isLoadingCompany={isLoadingCompany}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
              />
            </Tab>
          </Tabs>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}